import { Avatar, AvatarImage } from '@/components/Avatar';
import LetteredAvatar from '@/components/LetteredAvatar';
import { InstanceLink } from '@/components/routing';
import { SidebarTrigger, useSidebar } from '@/components/Sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Title } from '@/components/typography';
import { HEADER_HEIGHT } from '@/constants/dimensions';
import InstanceSwitcher from '@/features/InstanceSwitcher';
import { useHeaderContext } from '@/hooks/header/HeaderContext';
import { cn } from '@/lib/utils';
import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

import AISearch from './ai/AISearch';

const DEFAULT_TITLE = 'DEFAULT_PAGE_TITLE';

export type PageHeaderProps = Record<string, never>;

const PageHeader = () => {
  const user = useMe();
  const { title: menuTitle } = useHeaderContext();
  const { isMobile } = useSidebar();

  return (
    <header
      className={cn(
        'flex shrink-0 items-center leading-none',
        menuTitle ? 'border-b border-gray-100 bg-gray-100' : 'border-b-transparent bg-transparent',
      )}
      style={{ height: HEADER_HEIGHT }}
    >
      {isMobile && (
        <SidebarTrigger data-testid="sidebar-toggle" className="ms-2" size="icon-large" />
      )}
      {!!menuTitle && (
        <div className="flex min-w-0 flex-1 items-center gap-4 px-8 max-lg:px-2">
          <div className="min-w-0 flex-1 overflow-hidden">
            <Tooltip>
              <TooltipTrigger
                delay={1750}
                render={
                  <Title className="block w-full truncate" level="3" weight="regular">
                    {menuTitle || DEFAULT_TITLE}
                  </Title>
                }
              />
              <TooltipContent side="bottom" align="start">
                {menuTitle || DEFAULT_TITLE}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-4 max-sm:justify-end">
            <AISearch />
            <InstanceSwitcher />
            <InstanceLink
              className="rounded-full ring-gray-900/40 ring-offset-1 hover:drop-shadow-sm/25 focus-visible:ring-3"
              to={ROUTES.profile.path}
            >
              <Avatar size="medium">
                <AvatarImage
                  className="z-10"
                  src={user?.pictureUrl}
                  alt={user?.name || user?.email}
                />
                <LetteredAvatar
                  className="absolute"
                  name={user?.name}
                  size="large"
                  shape="circle"
                />
              </Avatar>
            </InstanceLink>
          </div>
        </div>
      )}
    </header>
  );
};

export default PageHeader;
